/**
 * Fallback implementation of @dls/react-colors.
 * Uses hardcoded smartQare brand guide color tokens.
 */

export type TonalRange = 'bright' | 'very-dark-gray' | string;

export type ThemeConfiguration = {
  color?: string;
  tone?: string;
  accent?: string;
};

export const smartQareBrandGuidePalette = {
  spacePurple: '#1B0954',
  royalPurple: '#4C359B',
  lavender: '#E5E1F6',
  lightBlue: '#CAD8EE',
  ochreOrange: '#E28A2B',
  white: '#FFFFFF',
  deepBlack: '#000000',
  logoPurple: '#522FA1',
} as const;

// Compatibility layer for existing imports. Use smartQareBrandGuidePalette
// for new features to keep names aligned with the guide.
export const smartQarePalette = {
  spacePurple: smartQareBrandGuidePalette.spacePurple,
  royalPurple: smartQareBrandGuidePalette.royalPurple,
  // Legacy alias kept to avoid touching all existing consumers at once.
  lila: smartQareBrandGuidePalette.royalPurple,
  lavender: smartQareBrandGuidePalette.lavender,
  lightBlue: smartQareBrandGuidePalette.lightBlue,
  ochreOrange: smartQareBrandGuidePalette.ochreOrange,
  white: smartQareBrandGuidePalette.white,
  deepBlack: smartQareBrandGuidePalette.deepBlack,
  logoPurple: smartQareBrandGuidePalette.logoPurple,
} as const;

// Extended smartQare palette for UI states that are not listed as standalone
// brand swatches in the guide (alpha overlays, subtle surfaces, disabled states).
export const smartQareExtendedPalette = {
  pageBackground: '#F4F1FB',
  surfaceSubtle: '#F8F6FC',
  textSecondary: 'rgba(27, 9, 84, 0.72)',
  separatorSoft: 'rgba(27, 9, 84, 0.14)',
  overlaySubtle: 'rgba(0, 0, 0, 0.4)',
  shadowLevelOne: 'rgba(0, 0, 0, 0.2)',
  shadowLevelTwo: 'rgba(0, 0, 0, 0.3)',
  shadowLevelThree: 'rgba(0, 0, 0, 0.4)',
  inputBorderDefault: 'rgba(27, 9, 84, 0.32)',
  inputBorderHover: 'rgba(27, 9, 84, 0.66)',
  inputBorderDisabled: 'rgba(27, 9, 84, 0.2)',
  inputBackgroundDisabled: 'rgba(202, 216, 238, 0.28)',
  textDisabled: 'rgba(27, 9, 84, 0.38)',
  hintText: 'rgba(27, 9, 84, 0.45)',
  textOnDarkSecondary: 'rgba(255, 255, 255, 0.8)',
  iconOnDark: 'rgba(255, 255, 255, 0.85)',
  iconSubtle: 'rgba(27, 9, 84, 0.6)',
  statusSuccess: 'rgba(0, 189, 94, 0.8)',
  statusNeutral: 'rgba(0, 0, 0, 0.3)',
  statusWarning: 'rgba(255, 131, 15, 0.8)',
} as const;

// Flat token map: componentColor(dls.x.y.z.state) returns a CSS color string.
// Values keep existing DLS-style access paths while using smartQare palette.
export const dls = {
  light: {
    lightCenter: { default: { normal: smartQarePalette.lavender } },
    lightEdge: { default: { normal: smartQarePalette.lila } },
  },
  loginScreen: {
    background: { default: { normal: smartQarePalette.spacePurple } },
    text: { default: { normal: smartQarePalette.white } },
  },
  contentItem: {
    text: {
      primary: { normal: smartQarePalette.spacePurple },
      secondary: { normal: smartQareExtendedPalette.textSecondary },
    },
  },
  separator: {
    background: { content: { normal: smartQareExtendedPalette.separatorSoft } },
  },
  listItem: {
    background: {
      default: {
        normalOn: smartQarePalette.lavender,
        normalOff: smartQarePalette.white,
        hover: smartQareExtendedPalette.pageBackground,
      },
    },
    text: {
      default: {
        normalOn: smartQarePalette.royalPurple,
        normalOff: smartQarePalette.spacePurple,
      },
    },
  },
  dimLayer: {
    background: { subtle: { normal: smartQareExtendedPalette.overlaySubtle } },
  },
  shadow: {
    shadow: {
      levelOne: { normal: smartQareExtendedPalette.shadowLevelOne },
      levelTwo: { normal: smartQareExtendedPalette.shadowLevelTwo },
      levelThree: { normal: smartQareExtendedPalette.shadowLevelThree },
    },
  },
  button: {
    background: { accent: { normal: smartQarePalette.royalPurple } },
  },
  textBox: {
    border: {
      default: {
        normal: smartQareExtendedPalette.inputBorderDefault,
        hover: smartQareExtendedPalette.inputBorderHover,
        focus: smartQarePalette.royalPurple,
        disabled: smartQareExtendedPalette.inputBorderDisabled,
      },
    },
    background: {
      default: {
        normal: smartQarePalette.white,
        disabled: smartQareExtendedPalette.inputBackgroundDisabled,
      },
    },
    text: {
      default: {
        normal: smartQarePalette.spacePurple,
        disabled: smartQareExtendedPalette.textDisabled,
      },
    },
    hintText: { default: { normal: smartQareExtendedPalette.hintText } },
  },
  popup: {
    background: { default: { normal: smartQarePalette.white } },
  },
};

// Identity function: the token is already the CSS value in this compat layer.
const componentColor = (token: string): string => token;

export default componentColor;
